import React, { useMemo, useState } from "react";
import { Grid, IconButton } from "@mui/material";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxButton from "components/UI/AcxButton";
import { stringToAllotment } from "components/Admin/AdminUIComponents/AudioFileSamplerV2/Views/RecommendationStep/StartSamplingDrawerContent";
import AudioMetadataModel from "models/AudioMetadataModel";
import theme from "Theme/AppTheme";
import { AcxViewSqlDialog } from "components/UI/AcxViewSqlDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AcxMenu, { StyledMenuLabel } from "components/UI/Menu/AcxMenu";
import MessageIcon from "@mui/icons-material/Message";

import { SignalDataEndpoint } from "components/Signals/SignalsModels";
import DrilldownStore from "./DrilldownStore";
import BaseObjectStore from "../BaseObjectStore";
import { DrilldownSource } from "./DrilldownTypes";

type Props = {
    drilldownStore: DrilldownStore | null;
    baseObjectStore: BaseObjectStore<any, any> & DrilldownSource;
};

const DrilldownDialog = observer(
    ({ drilldownStore, baseObjectStore }: Props) => {
        const navigate = useNavigate();
        const [moreMenuAnchorEl, setMoreMenuAnchorEl] =
            useState<null | HTMLElement>(null);
        const [showSqlDialog, setShowSqlDialog] = useState(false);
        const normalizedDataEndpoint = useMemo(() => {
            const endpoint = baseObjectStore.dataEndpoint;
            if (!endpoint) return undefined;
            if (endpoint.includes("?")) {
                return endpoint.substring(0, endpoint.indexOf("?")) as
                    | SignalDataEndpoint
                    | undefined;
            }
            return endpoint as SignalDataEndpoint;
        }, [baseObjectStore.dataEndpoint]);

        if (!drilldownStore) return null;

        const handleDrilldownSampling = () => {
            const selectedDrillEddyData =
                drilldownStore.selectedDataDgStore.selectedRows.map((row) => {
                    const splitFilePath = row.fileName.split("/");
                    const eddyInteractionFilePath =
                        splitFilePath[splitFilePath.length - 1];

                    return {
                        fileName: eddyInteractionFilePath,

                        id: row.audioMetadataId,
                    } as AudioMetadataModel;
                });

            drilldownStore.recommendationStepStore.tableStore.setItems(
                selectedDrillEddyData,
            );
            drilldownStore.recommendationStepStore.tableStore.setSelectedItems(
                selectedDrillEddyData.map((row) => row.id),
            );

            const existing = {
                selections: [],
                count: "0",
            };
            existing.count = stringToAllotment(
                drilldownStore.recommendationStepStore.tableStore.selectedItems.length.toString(),
            );

            drilldownStore.recommendationStepStore.hierarchyAssignmentDistribution.set(
                0,
                existing,
            );
            drilldownStore.selectedDataDgStore.setLoading(true);
            drilldownStore.recommendationStepStore.useSample(false, () => {
                drilldownStore.viewData();
            });

            drilldownStore.selectedDataDgStore.clearSelected();
            drilldownStore.recommendationStepStore.tableStore.clearSelectedItems();
        };

        const sampleButtonDisabled =
            drilldownStore.selectedDataDgStore.selectedRows.length === 0 ||
            drilldownStore.selectedDataDgStore.selectedRows.length > 10;

        const showSQL =
            !!drilldownStore.drilldownSql &&
            drilldownStore.authStore.isUserUltra();

        const showViewInConvosButton =
            normalizedDataEndpoint &&
            baseObjectStore.config.pointSelectActions.viewInConvos;

        function handleMoreMenuClick(
            event: React.MouseEvent<HTMLButtonElement>,
        ) {
            setMoreMenuAnchorEl(event.currentTarget);
        }

        function handleMoreMenuClose() {
            setMoreMenuAnchorEl(null);
        }

        return (
            <AcxDialog
                title={drilldownStore.drilldownDialogTitle || "Untitled"}
                subTitle={
                    drilldownStore.drilldownSubtitle
                        ? drilldownStore.drilldownSubtitle
                        : ""
                }
                dialogContentChildren={
                    <Grid
                        container
                        item
                        style={{
                            height: "606px",
                            width: "80vw",
                            maxWidth: "calc(1536px - 48px)",
                        }}
                    >
                        <Grid
                            item
                            container
                            alignItems={"center"}
                            justifyContent={"flex-end"}
                            xs={12}
                            gap={1}
                        >
                            {showViewInConvosButton && (
                                <AcxButton
                                    onClick={async () => {
                                        if (
                                            drilldownStore.selectedDataPoint ||
                                            drilldownStore.selectedTopic ||
                                            drilldownStore.selectedDriver
                                        ) {
                                            drilldownStore.viewInConvos();

                                            navigate(
                                                "/app/conversations?fromDomain=signals",
                                            );

                                            drilldownStore.closeDrilldownDialog();
                                        }
                                    }}
                                    startIcon={<MessageIcon />}
                                    id="view-in-conversations"
                                >
                                    View in Conversations
                                </AcxButton>
                            )}
                            {showSQL && (
                                <>
                                    <IconButton
                                        id={`signals-drilldown-Menu`}
                                        style={{
                                            color: theme.palette.black.main,
                                            height: "40px",
                                        }}
                                        onClick={handleMoreMenuClick}
                                        size="small"
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <AcxViewSqlDialog
                                        sqlStatements={drilldownStore.drilldownSql!.split(
                                            "==========",
                                        )}
                                        isModalOpen={showSqlDialog}
                                        onClose={() => setShowSqlDialog(false)}
                                    />
                                    <AcxMenu
                                        anchorElement={moreMenuAnchorEl}
                                        menuItems={[
                                            {
                                                id: "signals-show-sql-menu-item",
                                                props: {
                                                    onClick: () => {
                                                        setShowSqlDialog(true);
                                                        handleMoreMenuClose();
                                                    },
                                                    disabled:
                                                        drilldownStore.anyTaskLoading,
                                                },
                                                label: (
                                                    <StyledMenuLabel>
                                                        View SQL
                                                    </StyledMenuLabel>
                                                ),
                                            },
                                        ]}
                                        onMenuClose={handleMoreMenuClose}
                                    />
                                </>
                            )}
                        </Grid>

                        <AcxDataGrid
                            dataGridStore={drilldownStore.selectedDataDgStore}
                        />
                    </Grid>
                }
                isOpen={drilldownStore.showSelectedDataDialog}
                onClose={() => drilldownStore.closeDrilldownDialog()}
                maxWidth={false}
                disableEnforceFocus
                hideCancelButton={
                    drilldownStore.selectedTopic || drilldownStore.selectedDriver
                        ? true
                        : false
                }
            >
                {drilldownStore.selectedTopic ||
                drilldownStore.selectedDriver ? undefined : (
                    <AcxButton
                        onClick={() => handleDrilldownSampling()}
                        disabled={sampleButtonDisabled}
                        containerStyle={{
                            marginRight: "16px",
                        }}
                        style={{
                            ...(sampleButtonDisabled && {
                                backgroundColor: theme.palette.gray[100],
                                color: theme.palette.gray[400],
                            }),
                        }}
                    >
                        Sample Selected Files
                    </AcxButton>
                )}
            </AcxDialog>
        );
    },
);

export default DrilldownDialog;
